import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RorosArtifactService } from '@/services/RorosService';
import { RorosPersonaKnowledgeService } from '@/services/roros/artifacts.service';
import { rorosKeys } from './keys';
import type {
  CreateOutcomePayload,
  UpdateOutcomePayload,
  CreateMetricPayload,
  UpdateMetricPayload,
  UpdateMetricValuePayload,
  CreatePrinciplePayload,
  UpdatePrinciplePayload,
  CreateRoutineArtifactPayload,
  UpdateRoutineArtifactPayload,
  CreateInitiativePayload,
  UpdateInitiativePayload,
  CreateKnowledgeItemPayload,
  UpdateKnowledgeItemPayload,
  CreateLearningItemPayload,
  UpdateLearningItemPayload,
} from '@/types/roros/artifacts';

const STALE_TIME = 2 * 60_000;
const GC_TIME = 5 * 60_000;

// ─── Outcomes ──────────────────────────────────────────────────────────────

export function useRorosOutcomes(domainId: string, params?: Record<string, string>) {
  return useQuery({
    queryKey: [...rorosKeys.outcomes.byDomain(domainId), params] as const,
    queryFn: () => RorosArtifactService.listOutcomes(domainId, params),
    enabled: !!domainId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// F-Meeting-1 Wave 3a: org-scoped annual outcomes (AC-8.1)
export function useAnnualOutcomes(params?: Record<string, string>) {
  return useQuery({
    queryKey: ['roros', 'annual-outcomes', params] as const,
    queryFn: () => RorosArtifactService.listAnnualOutcomes(params),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// F-Meeting-1 Wave 3a: cascade work items for an outcome detail modal (AC-8.2)
export function useOutcomeCascadeWorkItems(outcomeId: string | null) {
  return useQuery({
    queryKey: ['roros', 'outcome-cascade-work-items', outcomeId] as const,
    queryFn: () => RorosArtifactService.listOutcomeCascadeWorkItems(outcomeId!),
    enabled: !!outcomeId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosOutcome(domainId: string, id: string) {
  return useQuery({
    queryKey: rorosKeys.outcomes.detail(domainId, id),
    queryFn: () => RorosArtifactService.getOutcome(domainId, id),
    enabled: !!domainId && !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosOutcomeCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domainId, payload }: { domainId: string; payload: CreateOutcomePayload }) =>
      RorosArtifactService.createOutcome(domainId, payload),
    onSuccess: (_data, { domainId }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.outcomes.byDomain(domainId) });
      qc.invalidateQueries({ queryKey: rorosKeys.tree.all });
    },
  });
}

export function useRorosOutcomeUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domainId, id, payload }: { domainId: string; id: string; payload: UpdateOutcomePayload }) =>
      RorosArtifactService.updateOutcome(domainId, id, payload),
    onSuccess: (_data, { domainId, id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.outcomes.byDomain(domainId) });
      qc.invalidateQueries({ queryKey: rorosKeys.outcomes.detail(domainId, id) });
      qc.invalidateQueries({ queryKey: rorosKeys.tree.all });
    },
  });
}

export function useRorosOutcomeDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domainId, id }: { domainId: string; id: string }) =>
      RorosArtifactService.deleteOutcome(domainId, id),
    onSuccess: (_data, { domainId }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.outcomes.byDomain(domainId) });
      qc.invalidateQueries({ queryKey: rorosKeys.tree.all });
    },
  });
}

// ─── Metrics ───────────────────────────────────────────────────────────────

/**
 * J7-W2 — cross-domain listing of every metric in the org. Backed by the
 * /api/v1/metrics endpoint. Used by the Role Contract editor's
 * Outcomes Ownership picker.
 */
export function useRorosAllMetricsForOrg(params?: Record<string, string>) {
  return useQuery({
    queryKey: [...rorosKeys.metrics.allForOrg, params] as const,
    queryFn: () => RorosArtifactService.listAllMetricsForOrg(params),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosMetrics(domainId: string, params?: Record<string, string>) {
  return useQuery({
    queryKey: [...rorosKeys.metrics.byDomain(domainId), params] as const,
    queryFn: () => RorosArtifactService.listMetrics(domainId, params),
    enabled: !!domainId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosMetric(domainId: string, id: string) {
  return useQuery({
    queryKey: rorosKeys.metrics.detail(domainId, id),
    queryFn: () => RorosArtifactService.getMetric(domainId, id),
    enabled: !!domainId && !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosMetricOutcomes(domainId: string, id: string) {
  return useQuery({
    queryKey: rorosKeys.metrics.outcomes(domainId, id),
    queryFn: () => RorosArtifactService.getMetricOutcomes(domainId, id),
    enabled: !!domainId && !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosMetricCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domainId, payload }: { domainId: string; payload: CreateMetricPayload }) =>
      RorosArtifactService.createMetric(domainId, payload),
    onSuccess: (_data, { domainId }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.metrics.byDomain(domainId) });
    },
  });
}

export function useRorosMetricUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domainId, id, payload }: { domainId: string; id: string; payload: UpdateMetricPayload }) =>
      RorosArtifactService.updateMetric(domainId, id, payload),
    onSuccess: (_data, { domainId, id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.metrics.byDomain(domainId) });
      qc.invalidateQueries({ queryKey: rorosKeys.metrics.detail(domainId, id) });
    },
  });
}

export function useRorosMetricDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domainId, id }: { domainId: string; id: string }) =>
      RorosArtifactService.deleteMetric(domainId, id),
    onSuccess: (_data, { domainId }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.metrics.byDomain(domainId) });
    },
  });
}

export function useRorosMetricUpdateValue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domainId, id, payload }: { domainId: string; id: string; payload: UpdateMetricValuePayload }) =>
      RorosArtifactService.updateMetricValue(domainId, id, payload),
    onSuccess: (_data, { domainId, id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.metrics.byDomain(domainId) });
      qc.invalidateQueries({ queryKey: rorosKeys.metrics.detail(domainId, id) });
      qc.invalidateQueries({ queryKey: rorosKeys.metrics.history(domainId, id) });
    },
  });
}

export function useRorosMetricHistory(domainId: string, id: string, limit?: number) {
  return useQuery({
    queryKey: [...rorosKeys.metrics.history(domainId, id), limit] as const,
    queryFn: () => RorosArtifactService.getMetricHistory(domainId, id, limit),
    enabled: !!domainId && !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosMetricLinkOutcome() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domainId, id, outcomeId }: { domainId: string; id: string; outcomeId: string }) =>
      RorosArtifactService.linkMetricOutcome(domainId, id, { outcome_id: outcomeId }),
    onSuccess: (_data, { domainId, id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.metrics.outcomes(domainId, id) });
    },
  });
}

export function useRorosMetricUnlinkOutcome() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domainId, id, outcomeId }: { domainId: string; id: string; outcomeId: string }) =>
      RorosArtifactService.unlinkMetricOutcome(domainId, id, outcomeId),
    onSuccess: (_data, { domainId, id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.metrics.outcomes(domainId, id) });
    },
  });
}

// ─── Principles ────────────────────────────────────────────────────────────

/**
 * J7-W4 task 6b — cross-domain principles listing for the new PrinciplesPage.
 * Calls GET /api/v1/principles. Returns every active principle in the org so
 * the page can group by domain client-side.
 */
export function useRorosAllPrinciplesForOrg(params?: Record<string, string>) {
  return useQuery({
    queryKey: [...rorosKeys.principles.allForOrg, params] as const,
    queryFn: () => RorosArtifactService.listAllPrinciplesForOrg(params),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosPrinciples(domainId: string, params?: Record<string, string>) {
  return useQuery({
    queryKey: [...rorosKeys.principles.byDomain(domainId), params] as const,
    queryFn: () => RorosArtifactService.listPrinciples(domainId, params),
    enabled: !!domainId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosPrinciple(domainId: string, id: string) {
  return useQuery({
    queryKey: rorosKeys.principles.detail(domainId, id),
    queryFn: () => RorosArtifactService.getPrinciple(domainId, id),
    enabled: !!domainId && !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosPrincipleCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domainId, payload }: { domainId: string; payload: CreatePrinciplePayload }) =>
      RorosArtifactService.createPrinciple(domainId, payload),
    onSuccess: (_data, { domainId }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.principles.byDomain(domainId) });
      qc.invalidateQueries({ queryKey: rorosKeys.principles.allForOrg });
    },
  });
}

export function useRorosPrincipleUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domainId, id, payload }: { domainId: string; id: string; payload: UpdatePrinciplePayload }) =>
      RorosArtifactService.updatePrinciple(domainId, id, payload),
    onSuccess: (_data, { domainId, id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.principles.byDomain(domainId) });
      qc.invalidateQueries({ queryKey: rorosKeys.principles.detail(domainId, id) });
      qc.invalidateQueries({ queryKey: rorosKeys.principles.allForOrg });
    },
  });
}

export function useRorosPrincipleDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domainId, id }: { domainId: string; id: string }) =>
      RorosArtifactService.deletePrinciple(domainId, id),
    onSuccess: (_data, { domainId }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.principles.byDomain(domainId) });
      qc.invalidateQueries({ queryKey: rorosKeys.principles.allForOrg });
    },
  });
}

// ─── Routine Artifacts ─────────────────────────────────────────────────────

/**
 * Cross-domain routines listing (NAV-3). Calls GET /api/v1/routines.
 * Used by RoutinesPage aggregate view.
 */
export function useRorosRoutines(params?: Record<string, string>) {
  return useQuery({
    queryKey: [...rorosKeys.routineArtifacts.all, params] as const,
    queryFn: () => RorosArtifactService.listAllRoutines(params),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosRoutineArtifacts(domainId: string, params?: Record<string, string>) {
  return useQuery({
    queryKey: [...rorosKeys.routineArtifacts.byDomain(domainId), params] as const,
    queryFn: () => RorosArtifactService.listRoutineArtifacts(domainId, params),
    enabled: !!domainId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosRoutineArtifact(domainId: string, id: string) {
  return useQuery({
    queryKey: rorosKeys.routineArtifacts.detail(domainId, id),
    queryFn: () => RorosArtifactService.getRoutineArtifact(domainId, id),
    enabled: !!domainId && !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosRoutineArtifactCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domainId, payload }: { domainId: string; payload: CreateRoutineArtifactPayload }) =>
      RorosArtifactService.createRoutineArtifact(domainId, payload),
    onSuccess: (_data, { domainId }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.routineArtifacts.byDomain(domainId) });
    },
  });
}

export function useRorosRoutineArtifactUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domainId, id, payload }: { domainId: string; id: string; payload: UpdateRoutineArtifactPayload }) =>
      RorosArtifactService.updateRoutineArtifact(domainId, id, payload),
    onSuccess: (_data, { domainId, id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.routineArtifacts.byDomain(domainId) });
      qc.invalidateQueries({ queryKey: rorosKeys.routineArtifacts.detail(domainId, id) });
      // Phase 4 fix-3: also invalidate the cross-domain `all` query used by
      // RoutinesPage. Without this, a successful PUT (e.g. self-QC toggle)
      // updates the server but the FE list keeps showing the stale value
      // because the query key didn't match.
      qc.invalidateQueries({ queryKey: rorosKeys.routineArtifacts.all });
    },
  });
}

export function useRorosRoutineArtifactDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domainId, id }: { domainId: string; id: string }) =>
      RorosArtifactService.deleteRoutineArtifact(domainId, id),
    onSuccess: (_data, { domainId }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.routineArtifacts.byDomain(domainId) });
    },
  });
}

export function useRorosRoutineArtifactArchive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => RorosArtifactService.archiveRoutineArtifact(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.routineArtifacts.all });
    },
  });
}

// ─── Initiatives ───────────────────────────────────────────────────────────

export function useRorosInitiatives(domainId: string, params?: Record<string, string>) {
  return useQuery({
    queryKey: [...rorosKeys.initiatives.byDomain(domainId), params] as const,
    queryFn: () => RorosArtifactService.listInitiatives(domainId, params),
    enabled: !!domainId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosInitiative(domainId: string, id: string) {
  return useQuery({
    queryKey: rorosKeys.initiatives.detail(domainId, id),
    queryFn: () => RorosArtifactService.getInitiative(domainId, id),
    enabled: !!domainId && !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosInitiativeInstances(domainId: string, id: string) {
  return useQuery({
    queryKey: rorosKeys.initiatives.instances(domainId, id),
    queryFn: () => RorosArtifactService.getInitiativeInstances(domainId, id),
    enabled: !!domainId && !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosInitiativeCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domainId, payload }: { domainId: string; payload: CreateInitiativePayload }) =>
      RorosArtifactService.createInitiative(domainId, payload),
    onSuccess: (_data, { domainId }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.initiatives.byDomain(domainId) });
    },
  });
}

export function useRorosInitiativeUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domainId, id, payload }: { domainId: string; id: string; payload: UpdateInitiativePayload }) =>
      RorosArtifactService.updateInitiative(domainId, id, payload),
    onSuccess: (_data, { domainId, id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.initiatives.byDomain(domainId) });
      qc.invalidateQueries({ queryKey: rorosKeys.initiatives.detail(domainId, id) });
    },
  });
}

export function useRorosInitiativeDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domainId, id }: { domainId: string; id: string }) =>
      RorosArtifactService.deleteInitiative(domainId, id),
    onSuccess: (_data, { domainId }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.initiatives.byDomain(domainId) });
    },
  });
}

/**
 * J6 — Convert a one-time initiative into a routine. Invalidates both the
 * initiatives list (status flips to converted_to_routine) and the routines
 * list (a new row appears).
 */
export function useRorosInitiativeConvertToRoutine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domainId, id }: { domainId: string; id: string }) =>
      RorosArtifactService.convertInitiativeToRoutine(domainId, id),
    onSuccess: (_data, { domainId, id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.initiatives.byDomain(domainId) });
      qc.invalidateQueries({ queryKey: rorosKeys.initiatives.detail(domainId, id) });
      qc.invalidateQueries({ queryKey: rorosKeys.routineArtifacts.byDomain(domainId) });
      qc.invalidateQueries({ queryKey: rorosKeys.routineArtifacts.all });
    },
  });
}

// ─── Knowledge ─────────────────────────────────────────────────────────────

export function useRorosKnowledge(domainId: string, params?: Record<string, string>) {
  return useQuery({
    queryKey: [...rorosKeys.knowledge.byDomain(domainId), params] as const,
    queryFn: () => RorosArtifactService.listKnowledge(domainId, params),
    enabled: !!domainId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosKnowledgeItem(domainId: string, id: string) {
  return useQuery({
    queryKey: rorosKeys.knowledge.detail(domainId, id),
    queryFn: () => RorosArtifactService.getKnowledge(domainId, id),
    enabled: !!domainId && !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosSystemKnowledge() {
  return useQuery({
    queryKey: rorosKeys.knowledge.system,
    queryFn: () => RorosArtifactService.listSystemKnowledge(),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosKnowledgeCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domainId, payload }: { domainId: string; payload: CreateKnowledgeItemPayload }) =>
      RorosArtifactService.createKnowledge(domainId, payload),
    onSuccess: (_data, { domainId }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.knowledge.byDomain(domainId) });
    },
  });
}

export function useRorosKnowledgeUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domainId, id, payload }: { domainId: string; id: string; payload: UpdateKnowledgeItemPayload }) =>
      RorosArtifactService.updateKnowledge(domainId, id, payload),
    onSuccess: (_data, { domainId, id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.knowledge.byDomain(domainId) });
      qc.invalidateQueries({ queryKey: rorosKeys.knowledge.detail(domainId, id) });
    },
  });
}

export function useRorosKnowledgeDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domainId, id }: { domainId: string; id: string }) =>
      RorosArtifactService.deleteKnowledge(domainId, id),
    onSuccess: (_data, { domainId }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.knowledge.byDomain(domainId) });
    },
  });
}

/**
 * J7-W4 task 6a (ART-02) — flips last_reviewed_at + review_due_at and emits
 * the GOV-05 audit event. Called from the KnowledgeDetailPage freshness
 * banner and the per-row "Mark reviewed" action on KnowledgeItemsPage.
 */
export function useRorosKnowledgeMarkReviewed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domainId, id }: { domainId: string; id: string }) =>
      RorosArtifactService.markKnowledgeReviewed(domainId, id),
    onSuccess: (_data, { domainId, id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.knowledge.byDomain(domainId) });
      qc.invalidateQueries({ queryKey: rorosKeys.knowledge.detail(domainId, id) });
      qc.invalidateQueries({ queryKey: ['roros', 'knowledge', 'due-for-review'] as const });
    },
  });
}

/** J7-W4 task 6a — items past or near review_due_at. `withinDays = 0` returns only past-due. */
export function useRorosKnowledgeDueForReview(withinDays: number = 0) {
  return useQuery({
    queryKey: rorosKeys.knowledge.dueForReview(withinDays),
    queryFn: () => RorosArtifactService.listKnowledgeDueForReview(withinDays),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

/**
 * S-1 — Force re-ingest of a knowledge item's attached documents.
 *
 * Bypasses the R1 idempotency skip-guard so a stuck/partially-indexed item
 * (e.g. all image OCR failed due to provider quota) can be re-run without
 * requiring an attachment re-add.
 *
 * On success invalidates the item detail query so the ExtractionStatusPanel
 * immediately reflects the new 'processing' status.
 */
export function useRorosKnowledgeReprocess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domainId, id }: { domainId: string; id: string }) =>
      RorosArtifactService.reprocessKnowledgeDocument(domainId, id),
    onSuccess: (_data, { domainId, id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.knowledge.detail(domainId, id) });
    },
  });
}

// ─── Learning ──────────────────────────────────────────────────────────────

export function useRorosLearning(domainId: string, params?: Record<string, string>) {
  return useQuery({
    queryKey: [...rorosKeys.learning.byDomain(domainId), params] as const,
    queryFn: () => RorosArtifactService.listLearning(domainId, params),
    enabled: !!domainId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosLearningItem(domainId: string, id: string) {
  return useQuery({
    queryKey: rorosKeys.learning.detail(domainId, id),
    queryFn: () => RorosArtifactService.getLearning(domainId, id),
    enabled: !!domainId && !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosLearningCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domainId, payload }: { domainId: string; payload: CreateLearningItemPayload }) =>
      RorosArtifactService.createLearning(domainId, payload),
    onSuccess: (_data, { domainId }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.learning.byDomain(domainId) });
    },
  });
}

export function useRorosLearningUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domainId, id, payload }: { domainId: string; id: string; payload: UpdateLearningItemPayload }) =>
      RorosArtifactService.updateLearning(domainId, id, payload),
    onSuccess: (_data, { domainId, id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.learning.byDomain(domainId) });
      qc.invalidateQueries({ queryKey: rorosKeys.learning.detail(domainId, id) });
    },
  });
}

export function useRorosLearningDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domainId, id }: { domainId: string; id: string }) =>
      RorosArtifactService.deleteLearning(domainId, id),
    onSuccess: (_data, { domainId }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.learning.byDomain(domainId) });
    },
  });
}

// ─── Persona Knowledge (pin/deny) hooks ───────────────────────────────────────
// AC-K-8/K-9: persona Documents tab consumes these to list/create/remove pins.

export function useRorosPersonaPinDenyList(
  orgId: string | undefined,
  personaId: string | undefined,
  mode?: 'pin' | 'deny',
) {
  return useQuery({
    queryKey: rorosKeys.knowledge.personaPinDeny(personaId ?? '', mode),
    queryFn: () => RorosPersonaKnowledgeService.listPinDeny(orgId!, personaId!, mode),
    enabled: !!orgId && !!personaId,
    staleTime: 60_000,
  });
}

export function useRorosPersonaReachableCount(
  orgId: string | undefined,
  personaId: string | undefined,
) {
  return useQuery({
    queryKey: rorosKeys.knowledge.personaReachableCount(personaId ?? ''),
    queryFn: () => RorosPersonaKnowledgeService.reachableCount(orgId!, personaId!),
    enabled: !!orgId && !!personaId,
    staleTime: 60_000,
  });
}

export function useRorosPersonaPinDenyCreate(orgId: string | undefined, personaId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { entityId: string; entityType: string; mode: 'pin' | 'deny' }) =>
      RorosPersonaKnowledgeService.createPinDeny(orgId!, personaId!, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.knowledge.personaPinDeny(personaId ?? '', 'pin') });
      qc.invalidateQueries({ queryKey: rorosKeys.knowledge.personaReachableCount(personaId ?? '') });
    },
  });
}

export function useRorosPersonaPinDenyDelete(orgId: string | undefined, personaId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ bindId }: { bindId: string }) =>
      RorosPersonaKnowledgeService.deletePinDeny(orgId!, personaId!, bindId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.knowledge.personaPinDeny(personaId ?? '', 'pin') });
      qc.invalidateQueries({ queryKey: rorosKeys.knowledge.personaReachableCount(personaId ?? '') });
    },
  });
}
